import * as l from '../lib/littlejs.esm.js';
import { init, update, updatePost, render, renderPost, isGoalReached } from './scenes/game.ts';
import { init as menuInit, update as menuUpdate, updatePost as menuUpdatePost, render as menuRender, renderPost as menuRenderPost } from './scenes/menu.ts';
import { tilesets } from './const/tiles.ts';
import ldtkData from '../assets/rex-and-chip.ldtk';
import { saveLevelData, loadLevelData } from './utils/storage.ts';
import Globals from './globals.ts';
// Configure engine options
l.setShowSplashScreen(false);
// Fix texture bleeding by shrinking tile slightly
l.setTileFixBleedScale(.5);
let gameState = 'menu';
// Make gameState accessible globally for entity checks
Globals.gameState = gameState;
let levels = ldtkData.levels.filter((l) => l.identifier.startsWith('Rex_')).sort((a, b) => parseInt(a.identifier.split('_')[1]) - parseInt(b.identifier.split('_')[1]));
let currentLevelIndex = 0;
let levelCompleteTimer = 0;
let levelStartTimer = 0;
let isPaused = false;
let pauseMenuSelected = 0; // 0: Resume, 1: Restart Level, 2: Menu
let pauseKeyCooldown = 0;
function loadLevel(levelIndex) {
    const rexLevel = levels[levelIndex];
    const chipLevel = ldtkData.levels.find((l) => l.identifier === `Chip_${levelIndex + 1}`);
    if (rexLevel) {
        init(rexLevel, chipLevel);
        gameState = 'levelStart';
        Globals.gameState = gameState;
        levelStartTimer = 5;
    }
}
const TILESIZE = 16;
function engineInit() {
    menuInit();
}
function engineUpdate() {
    gameState = Globals.gameState;
    if (gameState === 'levelStart') {
        levelStartTimer -= l.timeDelta;
        if (levelStartTimer <= 0) {
            gameState = 'game';
            Globals.gameState = gameState;
            // Start the level timer when gameplay begins
            Globals.levelStartTime = Date.now();
        }
        // Don't update ANYTHING during levelStart - no game logic, no entities, nothing
    }
    else if (gameState === 'game') {
        // Only update game logic if not in levelStart state
        update();
        if (isGoalReached()) {
            gameState = 'levelComplete';
            Globals.gameState = gameState;
            levelCompleteTimer = 0;
        }
    }
    else if (gameState === 'levelComplete') {
        levelCompleteTimer += l.timeDelta;
        if (levelCompleteTimer >= 2) { // 2 second delay
            // Save level completion data
            const existingData = loadLevelData(currentLevelIndex);
            const levelData = existingData || { completed: false };
            levelData.completed = true;
            if (!levelData.bestTime || Globals.levelCompletionTime < levelData.bestTime) {
                levelData.bestTime = Globals.levelCompletionTime;
            }
            saveLevelData(currentLevelIndex, levelData);
            currentLevelIndex++;
            if (currentLevelIndex < levels.length) {
                loadLevel(currentLevelIndex);
            }
            else {
                // Game completed - show level complete message before going to menu
                levelCompleteTimer = 0;
                gameState = 'gameCompleted';
            }
        }
    }
    else if (gameState === 'gameCompleted') {
        levelCompleteTimer += l.timeDelta;
        if (levelCompleteTimer >= 2) { // Additional 2 seconds for completion message
            gameState = 'menu';
            Globals.gameState = gameState;
            currentLevelIndex = 0; // Reset for next playthrough
        }
    }
    else {
        const action = menuUpdate();
        if (typeof action === 'object' && 'levelIndex' in action) {
            gameState = 'game';
            currentLevelIndex = action.levelIndex;
            loadLevel(currentLevelIndex);
        }
    }
}
function engineUpdatePost() {
    if (gameState === 'levelStart') {
        // Don't update ANYTHING during levelStart - no game logic, no camera, no HUD, nothing
    }
    else if (gameState === 'game') {
        // Only allow pause controls during actual gameplay, not during levelStart
        // Check for pause keys in updatePost too (always check)
        if (pauseKeyCooldown <= 0 && (l.keyWasPressed('Escape') || l.keyWasPressed('KeyP'))) { // Escape or P
            console.log(`Pause key pressed in updatePost: Escape or P. Toggling pause. Current isPaused: ${isPaused}`);
            isPaused = !isPaused;
            l.setPaused(isPaused);
            pauseKeyCooldown = 10; // Prevent immediate re-triggering
            if (isPaused) {
                pauseMenuSelected = 0; // Reset to first option when pausing
                console.log('Game paused. Menu selection reset to Resume.');
            }
            else {
                console.log('Game resumed.');
            }
        }
        // Decrement cooldown
        if (pauseKeyCooldown > 0) {
            pauseKeyCooldown--;
        }
        if (!isPaused) {
            updatePost();
        }
        else {
            // Handle pause menu input in updatePost when paused
            if (l.keyWasPressed('ArrowUp')) { // Up arrow
                const oldSelected = pauseMenuSelected;
                pauseMenuSelected = Math.max(0, pauseMenuSelected - 1);
                console.log(`ArrowUp pressed. Menu selection changed from ${oldSelected} to ${pauseMenuSelected}`);
            }
            if (l.keyWasPressed('ArrowDown')) { // Down arrow
                const oldSelected = pauseMenuSelected;
                pauseMenuSelected = Math.min(2, pauseMenuSelected + 1);
                console.log(`ArrowDown pressed. Menu selection changed from ${oldSelected} to ${pauseMenuSelected}`);
            }
            if (l.keyWasPressed('Enter') || l.keyWasPressed(' ')) { // Enter or Space
                console.log(`Select key pressed: Enter or Space. Selected option: ${pauseMenuSelected}`);
                if (pauseMenuSelected === 0) { // Resume
                    isPaused = false;
                    l.setPaused(false);
                    console.log('Resume selected. Game resumed.');
                }
                else if (pauseMenuSelected === 1) { // Restart Level
                    console.log('Restart Level selected. Reloading level.');
                    loadLevel(currentLevelIndex);
                    isPaused = false;
                    l.setPaused(false);
                }
                else if (pauseMenuSelected === 2) { // Menu
                    console.log('Menu selected. Returning to menu.');
                    gameState = 'menu';
                    Globals.gameState = gameState;
                    currentLevelIndex = 0;
                    isPaused = false;
                    l.setPaused(false);
                }
            }
        }
    }
    else {
        menuUpdatePost();
    }
}
function engineRender() {
    if (gameState === 'game') {
        render();
    }
    else if (gameState === 'levelComplete' || gameState === 'gameCompleted') {
        // Render game scene as backdrop
        render();
        // Render semi-transparent black overlay
        l.drawRect(l.mainCanvasSize.scale(0.5), l.mainCanvasSize, l.hsl(0, 0, 0, 0.5), 0, true, true);
        const message = gameState === 'gameCompleted' ? 'Game Complete!' : 'Level Complete!';
        l.drawTextScreen(message, l.vec2(l.mainCanvasSize.x / 2, l.mainCanvasSize.y / 2 + 100), 32, l.WHITE, 1, l.BLACK, 'center');
    }
    else if (gameState == 'menu') {
        menuRender();
    }
}
function engineRenderPost() {
    if (gameState === 'levelStart') {
        // Render game scene as backdrop
        render();
        // Render semi-transparent black overlay
        l.drawRect(l.mainCanvasSize.scale(0.5), l.mainCanvasSize, l.hsl(0, 0, 0, 0.2), 0, true, true);
        // Render level text
        l.drawTextScreen(`Level ${currentLevelIndex + 1}`, l.vec2(l.mainCanvasSize.x / 2, l.mainCanvasSize.y / 2 - 50), 48, l.WHITE, 1, l.BLACK, 'center');
        // Render countdown timer
        l.drawTextScreen(Math.ceil(levelStartTimer).toString(), l.vec2(l.mainCanvasSize.x / 2, l.mainCanvasSize.y / 2 + 50), 32, l.WHITE, 1, l.BLACK, 'center');
    }
    else if (gameState === 'game') {
        if (isPaused) {
            // Render pause overlay
            l.drawRect(l.mainCanvasSize.scale(0.5), l.mainCanvasSize, l.hsl(0, 0, 0, 0.5), 0, true, true);
            // Render pause menu using drawTextScreen
            const menuOptions = ['Resume', 'Restart Level', 'Menu'];
            const fontSize = 32;
            const centerY = l.mainCanvasSize.y / 2;
            for (let i = 0; i < menuOptions.length; i++) {
                const y = centerY - 50 + i * 40;
                const color = i === pauseMenuSelected ? l.YELLOW : l.WHITE;
                l.drawTextScreen(menuOptions[i], l.vec2(l.mainCanvasSize.x / 2, y), fontSize, color, 1, l.BLACK, 'center');
            }
        }
        else {
            renderPost();
        }
    }
    else if (gameState === 'levelComplete') {
        // Additional level complete UI
    }
    else {
        menuRenderPost();
    }
}
// Launch engine
l.engineInit(engineInit, engineUpdate, engineUpdatePost, engineRender, engineRenderPost, tilesets);
